import React, { useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Autocomplete,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
} from '@mui/icons-material';
import type { ChannelStep, ChannelIntegration, ContactWithUser } from '../../../types';

interface ChannelStepsEditorProps {
  steps: ChannelStep[];
  availableChannels: ChannelIntegration[];
  contacts: ContactWithUser[];
  onChange: (steps: ChannelStep[]) => void;
}

const ChannelStepsEditor: React.FC<ChannelStepsEditorProps> = ({
  steps,
  availableChannels,
  contacts,
  onChange,
}) => {
  const handleAdd = useCallback(() => {
    const defaultChannel = availableChannels[0]?.channel_id ?? '';
    onChange([...steps, { channel_id: defaultChannel, contact_username: null, description: '' }]);
  }, [steps, availableChannels, onChange]);

  const handleRemove = useCallback(
    (index: number) => {
      onChange(steps.filter((_, i) => i !== index));
    },
    [steps, onChange],
  );

  const handleUpdate = useCallback(
    (index: number, updates: Partial<ChannelStep>) => {
      const newSteps = [...steps];
      newSteps[index] = { ...newSteps[index], ...updates };
      onChange(newSteps);
    },
    [steps, onChange],
  );

  const handleMoveUp = useCallback(
    (index: number) => {
      if (index === 0) return;
      const newSteps = [...steps];
      [newSteps[index - 1], newSteps[index]] = [newSteps[index], newSteps[index - 1]];
      onChange(newSteps);
    },
    [steps, onChange],
  );

  const handleMoveDown = useCallback(
    (index: number) => {
      if (index >= steps.length - 1) return;
      const newSteps = [...steps];
      [newSteps[index], newSteps[index + 1]] = [newSteps[index + 1], newSteps[index]];
      onChange(newSteps);
    },
    [steps, onChange],
  );

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        Channel Steps
      </Typography>

      {steps.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontStyle: 'italic' }}>
          No channel steps configured
        </Typography>
      )}

      {steps.map((step, index) => (
        <Box
          key={`${step.channel_id}-${index}`}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            mb: 1.5,
            p: 1.5,
            borderRadius: 1,
            bgcolor: 'action.hover',
          }}
        >
          <Typography
            variant="caption"
            sx={{ mt: 1.5, fontWeight: 'bold', color: 'text.secondary', minWidth: 20 }}
          >
            {index + 1}.
          </Typography>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Channel</InputLabel>
            <Select
              value={step.channel_id}
              label="Channel"
              onChange={(e) => handleUpdate(index, { channel_id: e.target.value })}
            >
              {availableChannels.map((ch) => (
                <MenuItem key={ch.channel_id} value={ch.channel_id}>
                  {ch.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Autocomplete
            size="small"
            sx={{ minWidth: 180, flex: 1 }}
            options={contacts}
            getOptionLabel={(c) => c.name || c.email}
            value={contacts.find((c) => c.username === step.contact_username) ?? null}
            onChange={(_, newValue) =>
              handleUpdate(index, { contact_username: newValue?.username ?? null })
            }
            renderInput={(params) => <TextField {...params} label="Contact override" />}
          />

          <TextField
            size="small"
            label="Note"
            value={step.description}
            onChange={(e) => handleUpdate(index, { description: e.target.value })}
            sx={{ flex: 1 }}
          />

          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <IconButton size="small" onClick={() => handleMoveUp(index)} disabled={index === 0}>
              <ArrowUpIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => handleMoveDown(index)}
              disabled={index >= steps.length - 1}
            >
              <ArrowDownIcon fontSize="small" />
            </IconButton>
          </Box>

          <IconButton size="small" onClick={() => handleRemove(index)} color="error">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}

      <Button size="small" startIcon={<AddIcon />} onClick={handleAdd} sx={{ mt: 0.5 }}>
        Add Step
      </Button>
    </Box>
  );
};

export default ChannelStepsEditor;
